import { useQuery, useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { handleGetRequest, handlePostRequest } from '../../utils/api-handler.util'
import { useAuth } from '../../contexts/AuthContext'
import { PaperAirplaneIcon } from '@heroicons/react/24/solid'
import { formatTime } from '../../utils/date.util'
import BackButtonAndTitle from '../../components/retailer-shop/BackButtonAndTitle'
import { MESSAGE_REFETCH_INTERVAL_MILLIS } from '../../constants/messagingConstants'
import { SUPPORT_TICKET_STATUS } from '../../constants/supportTicket'
import SeoHelmet from '../../components/common/SeoHelmet'

const SupportTicketMessage = () => {
  const { ticketId } = useParams()
  const { currentUser } = useAuth()

  const { data: ticketData } = useQuery({
    queryKey: QueryKeys.supportTicketDetails(ticketId),
    queryFn: () => handleGetRequest(`/${currentUser.userType}s/support-tickets/${ticketId}`),
    refetchInterval: (queryData) => {
      const ticket = queryData?.state?.data?.data || {}
      return ticket?.status !== SUPPORT_TICKET_STATUS.resolved
        ? MESSAGE_REFETCH_INTERVAL_MILLIS
        : false
    },
  })
  const ticket = ticketData?.data
  const [message, setMessage] = useState('')

  const respondMutation = useMutation({
    mutationFn: () =>
      handlePostRequest(`/${currentUser.userType}s/support-tickets/${ticketId}/respond`, {
        message,
      }),
    onSuccess: () => {
      setMessage('')
      QueryKeys.supportTicketDetails.invalidate(ticketId)
      QueryKeys.supportTickets.invalidateAll()
    },
  })
  const handleSendMessage = async () => {
    if (!message.trim()) return
    await respondMutation.mutateAsync()
  }

  return (
    <div className="main-container max-w-2xl mx-auto relative min-h-screen flex flex-col gap-4 bg-background-secondary">
      <BackButtonAndTitle title="Support Ticket Details" />
      <SeoHelmet
        title={ticket?.subject || 'Support Ticket'}
        titleSuffix="Support"
        description={ticket?.description || 'View and respond to your support ticket.'}
        keywords="support ticket, customer support, help desk, Sokooni support"
        url={`/support/tickets/${ticketId}`}
      />
      <div className="bg-background border border-orange-200 rounded-lg flex flex-row items-center px-6 py-4 shadow">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-lg text-foreground-secondary">
                {ticket?.subject}
              </span>
              {ticket?.status && (
                <span className="ml-2 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 capitalize">
                  {ticket.status}
                </span>
              )}
            </div>
            <span className="italic font-medium text-sm text-secondary-600 ml-4 whitespace-nowrap">
              {ticket?.code}
            </span>
          </div>
          <div className="text-sm text-medium-opacity mt-1 truncate">{ticket?.description}</div>
        </div>
      </div>
      <div className=" flex-1 rounded-xl flex flex-col">
        <div className="flex-1 flex flex-col gap-4 px-4 py-6 overflow-y-auto">
          {ticket?.messages?.map((msg, idx) => {
            const isCurrentUser = msg.senderType === currentUser.userType
            return (
              <div
                key={idx}
                className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`
          ${
            isCurrentUser
              ? 'bg-tertiary-600 text-white rounded-2xl rounded-tr-none'
              : 'bg-background text-black rounded-2xl rounded-tl-none'
          }
          px-5 py-3 max-w-[80%] text-base font-normal
        `}
                >
                  {msg.message}
                </div>
                <div className="text-xs text-black/50 mt-1 px-2">
                  {formatTime(msg.sentAt)} – {isCurrentUser ? 'me' : 'admin'}
                </div>
              </div>
            )
          })}
        </div>
        <div className="bg-background h-20 flex items-center px-4 gap-2">
          <input
            className="flex-1 bg-[#FAF8F5] border border-gray-300 rounded-full px-4 py-2 text-sm outline-none"
            placeholder="Type here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <button
            className="bg-secondary-600 rounded-full w-10 h-10 flex items-center justify-center"
            onClick={handleSendMessage}
            disabled={respondMutation.isLoading || !message.trim()}
          >
            <PaperAirplaneIcon className="w-6 h-6 text-white rotate-30" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default SupportTicketMessage
